/**
 * 매장 분류 두 축과 상설 토글 (VMD-403).
 *
 * 매장 구분은 지금까지 `store_type` 한 값이었고, 그 값이 면세와 아울렛과 상설과
 * 계약형태 네 축을 하나로 접은 파생값이라 서로 다른 분류가 서로를 가렸다. 운영
 * 실측(2026-10-26, 영업중 749곳)으로 계약형태 원값은 대리점 343 / 백화점 308 /
 * 직영점 97 인데 `store_type` 에서는 그보다 적게 보이고, 백화점아울렛 122곳 중
 * 유통채널 07 로 아울렛에 드는 것은 43곳뿐이다. 그래서 두 축을 함께 보여 주고
 * 각각으로 거른다.
 *
 * 축이 셋이다. 계약형태(`contract_type_cd`), 매장형태(`analytic_store_type`),
 * 그리고 상설(`is_permanent`). 상설은 앞의 두 축 어느 쪽도 아니라 토글이다 —
 * 상설 117곳이 여섯 매장형태에 흩어져 있고 그중 70곳이 백화점아울렛이어서,
 * 배타 칩으로 두면 백화점아울렛의 절반 이상이 아울렛으로 보이지 않는다.
 *
 * ## 라벨이 왜 백엔드가 아니라 여기에 있는가
 *
 * `store_type_name` 은 `common_code` 조인으로 백엔드가 내려 주지만 새 두 축은
 * 그렇게 하지 않는다. 필터 칩이 한글 라벨에서 코드를 찾는 방향을 이미 프런트에서
 * 쥐고 있어서(`reuseQueryString` 의 `typeJSON`), 표시 라벨만 백엔드로 옮기면 같은
 * 매핑의 출처가 둘이 되어 갈라진다. 그리고 `common_code` 의 기존 조인이 복합 PK 의
 * 절반(`code_id`)만 쓰고 `code_group_name` 을 보지 않아서, 새 코드 그룹에 `A` 같은
 * 맨글자를 넣으면 지금의 `store_type` 라벨 조인이 여러 행에 걸린다.
 *
 * ## 선택값에 축 이름을 붙이는 이유
 *
 * 필터의 선택 상태는 한글 라벨을 키로 쓴다. 계약형태 줄과 매장형태 줄에 `백화점`
 * 이 둘 다 있어서 라벨만으로는 두 줄을 구분할 수 없다. 그래서 선택값은
 * `계약형태 백화점` 처럼 축을 앞에 붙인 문자열로 들고 다닌다. 모달 안의 칩은
 * 라벨만 보여 주고, 목록 상단의 삭제 가능한 칩은 축까지 보여 준다.
 */

export const CONTRACT_AXIS = "계약형태";
export const SHOP_TYPE_AXIS = "매장형태";

/** 계약형태 원값(`SHOP_CTRT_TYP_CD`). 영업중 매장에는 이 세 값만 나온다. */
export const CONTRACT_TYPE_LABELS: Record<string, string> = {
  A: "백화점",
  B: "대리점",
  C: "직영점",
};

/**
 * 분석계기준(Lv.1) 매장형태(Lv.3) 등록 라벨. 2026-10-13 에 ERP팀에서 받은
 * `BIM_SHOP_CLS` 등록 값이다. 추론이 아니라 등록 라벨이므로 임의로 바꾸지 말 것.
 *
 * `E` 는 상위 가지가 둘이라(온/오프라인) 코드만으로 라벨이 완전히 정해지지는
 * 않는다. 영업중 2곳이 모두 오프라인이라 지금은 `기타` 가 맞지만, 이 표를 근거로
 * `E` 의 뜻을 단정하지 말 것.
 */
export const SHOP_TYPE_LABELS: Record<string, string> = {
  A: "백화점",
  S: "가두",
  L: "백화점아울렛",
  M: "쇼핑몰",
  D: "면세",
  W: "홀세일",
  E: "기타",
  N: "온라인",
};

/**
 * 필터 칩으로 내보내는 매장형태.
 *
 * 여덟 값을 전부 칩으로 만들지 않는다 — 운영 실측(2026-10-26, 영업중 749곳)으로
 * 홀세일 4, 기타 2, 온라인 1 이라 상시 비어 있는 칩이 된다. 팩토리 칩이 그래서
 * 대리점 목록을 냈던 선례(VMD-396)를 되풀이하지 않는다. 다섯 칩이 749곳 중
 * 742곳을 덮는다.
 *
 * **칩에서 접는 것과 값을 숨기는 것은 다르다.** 칩에 없는 세 값도 매장을 열면
 * 등록 라벨 그대로 보여 준다 — 그것이 `SHOP_TYPE_LABELS` 가 여덟 값을 들고 있는
 * 이유다.
 */
export const SHOP_TYPE_FILTER_CODES = ["A", "S", "L", "M", "D"];

/** 상설 토글이 쓰는 라벨. 칩이 아니라 켜고 끄는 토글이다. */
export const PERMANENT_LABEL = "상설만";

/**
 * 아울렛 태그에 쓰는 라벨.
 *
 * 태그를 달지 말지 판단할 때 기존 `store_type_name` 과 비교하는 데도 쓴다 —
 * 그 라벨은 백엔드 `common_code` 조인 결과라 프런트가 보장하지 못하므로,
 * `store_type='OL'` 이면 라벨이 항상 '아울렛' 이라고 가정하지 않고 실제 값을 본다.
 */
export const OUTLET_LABEL = "아울렛";

const labelsToChips = (labels: Record<string, string>, codes?: string[]) => (codes ?? Object.keys(labels)).map((code) => labels[code]);

/** 계약형태 칩 (백화점, 대리점, 직영점) */
export const CONTRACT_TYPE_CHIPS = labelsToChips(CONTRACT_TYPE_LABELS);

/** 매장형태 칩 (백화점, 가두, 백화점아울렛, 쇼핑몰, 면세) */
export const SHOP_TYPE_CHIPS = labelsToChips(SHOP_TYPE_LABELS, SHOP_TYPE_FILTER_CODES);

/** 선택값에 축 이름을 붙인다. `계약형태 백화점` */
export const qualify = (axis: string, label: string) => `${axis} ${label}`;

/** 축 접두가 붙은 선택값인지 */
export const isQualified = (value: string) => typeof value === "string" && (value.startsWith(`${CONTRACT_AXIS} `) || value.startsWith(`${SHOP_TYPE_AXIS} `));

/** 축 접두를 떼고 라벨만. 접두가 없으면 그대로 돌려준다. */
export const stripAxis = (value: string) => {
  for (const axis of [CONTRACT_AXIS, SHOP_TYPE_AXIS]) {
    if (typeof value === "string" && value.startsWith(`${axis} `)) {
      return value.slice(axis.length + 1);
    }
  }
  return value;
};

/** 축 접두가 붙은 선택값의 축 이름. 접두가 없으면 빈 문자열. */
export const axisOf = (value: string) => {
  for (const axis of [CONTRACT_AXIS, SHOP_TYPE_AXIS]) {
    if (typeof value === "string" && value.startsWith(`${axis} `)) {
      return axis;
    }
  }
  return "";
};

const codeOf = (labels: Record<string, string>, label: string) => Object.keys(labels).find((code) => labels[code] === label);

/**
 * 축 접두가 붙은 선택값을 코드로 바꾼다. 축을 먼저 보고 그 축의 표에서만 찾으므로
 * 두 축에 같은 라벨(`백화점`)이 있어도 섞이지 않는다. 모르는 값은 `undefined`.
 */
export const codeOfQualified = (value: string) => {
  const axis = axisOf(value);
  const label = stripAxis(value);
  if (axis === CONTRACT_AXIS) return codeOf(CONTRACT_TYPE_LABELS, label);
  if (axis === SHOP_TYPE_AXIS) return codeOf(SHOP_TYPE_LABELS, label);
  return undefined;
};

/** 매장 한 곳의 계약형태 표시 라벨. 값이 없거나 모르는 코드면 빈 문자열. */
export const contractTypeName = (code?: string | null) => (code && CONTRACT_TYPE_LABELS[code]) || "";

/** 매장 한 곳의 매장형태 표시 라벨. 칩에서 접은 세 값도 등록 라벨로 보여 준다. */
export const shopTypeName = (code?: string | null) => (code && SHOP_TYPE_LABELS[code]) || "";

/**
 * 아울렛 판정.
 *
 * 매장형태 `L` 백화점아울렛 **또는** 유통채널 07 직영아울렛이다. 유통채널 07 은
 * 프런트에 숫자로 오지 않고 `store_type = 'OL'` 로 보인다(크론과 로그인 경로가
 * `SAP_CH_CD='07'` 을 `OL` 로 접어서 쓴다).
 *
 * 운영 실측(2026-10-26)으로 `OL` 46곳 중 매장형태가 `L` 이 아닌 매장은 X30046
 * LF스퀘어광양(직) 한 곳이고 매장형태가 `A` 백화점이다. 요청자가 2026-10-07 에
 * 직영아울렛은 지금처럼 아울렛으로 보이면 된다고 확인한 매장이고 VMD-396 이 막
 * 고친 지점이라, 이 `또는` 을 빼면 그 매장이 아울렛에서 다시 빠진다.
 */
export const isOutlet = (store: { analytic_store_type?: string | null; store_type?: string | null }) =>
  store.analytic_store_type === "L" || store.store_type === "OL";

/**
 * 아울렛이지만 **매장형태 축으로는 드러나지 않는** 매장.
 *
 * 매장형태가 `L` 이면 라벨이 이미 `백화점아울렛` 이라 따로 표시할 것이 없다.
 * 유통채널 07 쪽으로만 아울렛인 매장은 매장형태 태그가 `백화점` 으로 떠서,
 * 아울렛으로 걸러 놓고 태그는 백화점인 불일치가 생긴다. 그 매장에만 별도
 * 아울렛 태그를 단다 — 매장형태 라벨을 덮어쓰지 않는 것이 중요하다. 등록된
 * 매장형태는 실제로 `A` 백화점이고 아울렛인 근거는 유통채널이라는 다른 축이다.
 */
export const isChannelOutlet = (store: { analytic_store_type?: string | null; store_type?: string | null }) =>
  isOutlet(store) && store.analytic_store_type !== "L";
